import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { journeyAPI } from '@/services/api';
import JourneyHorizontal from './JourneyHorizontal';
import {
  CheckCircle2, Circle, Clock, XCircle, ChevronLeft, FileText, FileCheck2,
  Upload, MessageSquare, AlertTriangle, Loader2, Send, Building2, User, Briefcase, ListChecks,
  ArrowRight, CornerDownRight, Shuffle, PlayCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/uploads/')) return `${API_URL}${url}`;
  return url;
};

/**
 * Shared candidate journey detail view.
 * Props:
 *   role: 'recruiter' | 'applicant'
 */
export default function CandidateJourneyDetail({ role }) {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null); // { type, payload }

  const load = useCallback(async () => {
    try { setJourney(await journeyAPI.get(matchId)); setError(''); }
    catch (e) { setError(e?.response?.data?.detail || 'Failed to load journey'); }
    finally { setLoading(false); }
  }, [matchId]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (fn) => {
    setActionLoading(true);
    try { const r = await fn(); setJourney(r); setModal(null); }
    catch (e) { alert(e?.response?.data?.detail || 'Action failed'); }
    finally { setActionLoading(false); }
  };

  if (loading) return <div className="text-slate-500 dark:text-zinc-500 text-sm">Loading journey…</div>;
  if (error) return <div className="text-red-600 dark:text-red-400 text-sm">{error}</div>;
  if (!journey) return null;

  const finalRejected = journey.final_outcome === 'rejected';
  const finalSelected = journey.final_outcome === 'selected';
  const stageOrder = journey.stages.map(s => s.key);
  const currentIdx = stageOrder.indexOf(journey.current_stage);
  const isRecruiter = role === 'recruiter';
  const backLink = isRecruiter ? `/recruiter/jobs/${journey.job_id}/journey` : '/applicant/journey';

  return (
    <div data-testid="candidate-journey-detail" className="max-w-4xl">
      <button onClick={() => navigate(backLink)} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white text-sm flex items-center gap-1 mb-4" data-testid="journey-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      {/* Header */}
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-2xl p-6 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-zinc-500 mb-2">
              <Briefcase className="w-3.5 h-3.5" /> <span>{journey.job_title}</span>
              <span className="text-slate-600 dark:text-zinc-700">·</span>
              <User className="w-3.5 h-3.5" /> <span>{journey.candidate?.name || 'Candidate'}</span>
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
              {isRecruiter ? journey.candidate?.name : journey.job_title}
            </h1>
            <p className="text-slate-500 dark:text-zinc-500 text-sm">
              {isRecruiter ? journey.candidate?.email : `Match score: ${Math.round(journey.match_percentage || 0)}%`}
            </p>
          </div>
          <OutcomeBadge journey={journey} />
        </div>
      </div>

      {/* Horizontal journey map (visual overview) */}
      <JourneyHorizontal journey={journey} />

      {/* Auto-interview hold — applicant-facing friendly banner (never exposes wallet reason) */}
      {!isRecruiter && journey.on_hold_message && !finalRejected && !finalSelected && (
        <div
          className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 mb-4 flex items-start gap-3"
          data-testid="applicant-on-hold-banner"
        >
          <Clock className="w-4 h-4 text-amber-700 dark:text-amber-300 mt-0.5" />
          <div>
            <p className="text-amber-800 dark:text-amber-200 text-sm font-medium">On hold by the organization</p>
            <p className="text-amber-700 dark:text-amber-300/90 text-xs">{journey.on_hold_message}</p>
          </div>
        </div>
      )}

      {/* Applicant — AI screening not yet scheduled by recruiter. Explains why the journey looks "stuck". */}
      {!isRecruiter && !journey.on_hold_message && !finalRejected && !finalSelected
        && ['screened', 'ai_screening'].includes(journey.current_stage)
        && !journey.ai_screening_scheduled && !journey.ai_screening_completed && (
        <div
          className="bg-sky-500/10 border border-sky-500/30 rounded-2xl p-4 mb-4 flex items-start gap-3"
          data-testid="applicant-awaiting-screening-banner"
        >
          <Clock className="w-4 h-4 text-sky-700 dark:text-sky-300 mt-0.5" />
          <div>
            <p className="text-sky-800 dark:text-sky-200 text-sm font-medium">Waiting on the recruiter to schedule your AI screening</p>
            <p className="text-sky-700 dark:text-sky-300/90 text-xs">Your resume has been matched. The recruiter is yet to schedule your AI screening round — you'll be notified the moment it begins.</p>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-2xl p-6 mb-4">
        <h2 className="text-sm uppercase tracking-widest text-slate-500 dark:text-zinc-500 mb-4 flex items-center gap-2"><ListChecks className="w-4 h-4" /> Journey</h2>
        <ol className="space-y-3" data-testid="journey-timeline">
          {journey.stages.map((s, i) => {
            const stoppedHere = finalRejected && i === currentIdx;
            return (
              <li key={s.key} className="flex items-start gap-3" data-testid={`stage-${s.key}`}>
                <StageDot stage={s} stoppedHere={stoppedHere} finalSelected={finalSelected} />
                <div className="flex-1 pb-2 border-b border-slate-200 dark:border-zinc-800/40 last:border-0">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className={`text-sm font-medium ${s.done ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-zinc-500'}`}>{s.label}</p>
                    {s.entered_at && <span className="text-[11px] text-slate-500 dark:text-zinc-600">{new Date(s.entered_at).toLocaleString()}</span>}
                  </div>
                  {s.comment && (
                    <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1 flex items-start gap-1.5">
                      <CornerDownRight className="w-3 h-3 mt-0.5 text-slate-500 dark:text-zinc-600 shrink-0" />
                      <span className="italic">&ldquo;{s.comment}&rdquo;</span>
                      {s.by && <span className="text-slate-500 dark:text-zinc-600 ml-1">— {s.by}</span>}
                    </p>
                  )}
                  {stoppedHere && (
                    <div className="mt-1 space-y-0.5">
                      <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> Application ended at this stage</p>
                      {journey.rejection_reason && (
                        <p className="text-[11.5px] text-red-500 dark:text-red-400/90 pl-4 italic">
                          Reason: {journey.rejection_reason}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Documents & Offer panels */}
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <DocumentsPanel journey={journey} role={role} onUpload={(file, name) => runAction(() => journeyAPI.uploadDocument(matchId, file, name))} />
        <OfferLetterPanel journey={journey} role={role}
          onUploadUnsigned={(file, comment) => runAction(() => journeyAPI.uploadOffer(matchId, file, comment))}
          onUploadSigned={(file) => runAction(() => journeyAPI.signOffer(matchId, file))} />
      </div>

      {/* Recruiter actions */}
      {isRecruiter && (
        <RecruiterActions journey={journey} actionLoading={actionLoading}
          onAdvance={(comment) => runAction(() => journeyAPI.advance(matchId, comment))}
          onSelect={(comment) => runAction(() => journeyAPI.decide(matchId, 'selected', comment))}
          onReject={(comment, reason) => runAction(() => journeyAPI.decide(matchId, 'rejected', comment, reason))}
          onRequestDocs={(docs, comment) => runAction(() => journeyAPI.requestDocuments(matchId, docs, comment))}
          onComment={(comment) => runAction(() => journeyAPI.comment(matchId, comment))}
          onSetStage={(payload) => runAction(() => journeyAPI.setStage(matchId, payload))}
          onStartAiScreening={async () => {
            setActionLoading(true);
            try {
              const { screeningAPI } = await import('@/services/api');
              await screeningAPI.scheduleScreening(matchId);
              await load();
              alert('AI screening scheduled. The candidate has been notified.');
            } catch (e) { alert(e?.response?.data?.detail || 'Failed to start AI screening'); }
            finally { setActionLoading(false); }
          }}
          openModal={setModal} modal={modal} />
      )}

      {/* Timeline of events */}
      <EventsTimeline events={journey.events} />
    </div>
  );
}

function StageDot({ stage, stoppedHere, finalSelected }) {
  if (stoppedHere) {
    return <span className="w-6 h-6 rounded-full bg-red-500/20 text-red-600 dark:text-red-400 flex items-center justify-center mt-0.5"><XCircle className="w-3.5 h-3.5" /></span>;
  }
  if (stage.done) {
    const cls = finalSelected && stage.key === 'offer_signed' ? 'bg-emerald-500/30 text-emerald-700 dark:text-emerald-300' : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400';
    return <span className={`w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${cls}`}><CheckCircle2 className="w-3.5 h-3.5" /></span>;
  }
  if (stage.current) {
    return <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 flex items-center justify-center mt-0.5 animate-pulse"><Clock className="w-3.5 h-3.5" /></span>;
  }
  return <span className="w-6 h-6 rounded-full bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-600 flex items-center justify-center mt-0.5"><Circle className="w-3 h-3" /></span>;
}

function OutcomeBadge({ journey }) {
  if (journey.final_outcome === 'selected') {
    return <span className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5" data-testid="outcome-selected"><CheckCircle2 className="w-3.5 h-3.5" /> Selected</span>;
  }
  if (journey.final_outcome === 'rejected') {
    return <span className="text-xs px-3 py-1.5 rounded-full bg-red-500/15 text-red-700 dark:text-red-300 flex items-center gap-1.5" data-testid="outcome-rejected"><XCircle className="w-3.5 h-3.5" /> Rejected</span>;
  }
  return <span className="text-xs px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 flex items-center gap-1.5" data-testid="outcome-pending"><Clock className="w-3.5 h-3.5" /> In progress</span>;
}

function DocumentsPanel({ journey, role, onUpload }) {
  const isApplicant = role === 'applicant';
  const docs = journey.documents_required || [];
  const uploaded = journey.documents_uploaded || [];
  const uploadedByName = new Map(uploaded.map(u => [u.document_name?.toLowerCase(), u]));

  if (docs.length === 0 && uploaded.length === 0) {
    return (
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-2xl p-5">
        <h3 className="text-sm font-medium text-slate-600 dark:text-zinc-300 mb-2 flex items-center gap-1.5"><FileText className="w-4 h-4 text-slate-500 dark:text-zinc-500" /> Documents</h3>
        <p className="text-xs text-slate-500 dark:text-zinc-600">No documents requested yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-2xl p-5" data-testid="documents-panel">
      <h3 className="text-sm font-medium text-slate-600 dark:text-zinc-300 mb-3 flex items-center gap-1.5"><FileText className="w-4 h-4 text-slate-500 dark:text-zinc-500" /> Documents required</h3>
      <ul className="space-y-2">
        {docs.map((d, i) => {
          const found = uploadedByName.get(d.toLowerCase());
          return (
            <li key={i} className="flex items-center justify-between gap-2">
              <span className="text-xs text-slate-600 dark:text-zinc-300 flex items-center gap-2">
                {found ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> : <Circle className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-600" />}
                {d}
              </span>
              {found ? (
                <a href={resolveUrl(found.url)} target="_blank" rel="noreferrer" className="text-[11px] text-sky-700 dark:text-sky-300 hover:underline">View</a>
              ) : isApplicant ? (
                <DocumentUploadButton documentName={d} onUpload={onUpload} />
              ) : (
                <span className="text-[11px] text-slate-500 dark:text-zinc-600">Pending</span>
              )}
            </li>
          );
        })}
      </ul>
      {uploaded.filter(u => !docs.map(d => d.toLowerCase()).includes(u.document_name?.toLowerCase())).length > 0 && (
        <>
          <h4 className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-500 mt-4 mb-1">Other uploads</h4>
          <ul className="space-y-1.5">
            {uploaded.filter(u => !docs.map(d => d.toLowerCase()).includes(u.document_name?.toLowerCase())).map((u, i) => (
              <li key={i} className="text-xs text-slate-500 dark:text-zinc-400 flex items-center justify-between">
                <span>{u.document_name}</span>
                <a href={resolveUrl(u.url)} target="_blank" rel="noreferrer" className="text-sky-700 dark:text-sky-300 hover:underline">View</a>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function DocumentUploadButton({ documentName, onUpload }) {
  const inputRef = React.useRef();
  return (
    <>
      <button onClick={() => inputRef.current?.click()} className="text-[11px] px-2 py-1 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 flex items-center gap-1" data-testid={`doc-upload-${documentName.replace(/\s+/g, '-').toLowerCase()}`}>
        <Upload className="w-3 h-3" /> Upload
      </button>
      <input ref={inputRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f, documentName); e.target.value = ''; }} />
    </>
  );
}

function OfferLetterPanel({ journey, role, onUploadUnsigned, onUploadSigned }) {
  const isRecruiter = role === 'recruiter';
  const isApplicant = role === 'applicant';
  const unsignedRef = React.useRef();
  const signedRef = React.useRef();
  const offer = journey.offer_letter;
  const signed = journey.signed_offer;
  const canReleaseOffer = journey.final_outcome === 'selected';
  const blocked = journey.final_outcome === 'rejected';

  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-2xl p-5" data-testid="offer-letter-panel">
      <h3 className="text-sm font-medium text-slate-600 dark:text-zinc-300 mb-3 flex items-center gap-1.5"><FileCheck2 className="w-4 h-4 text-slate-500 dark:text-zinc-500" /> Offer letter</h3>

      {blocked ? (
        <p className="text-xs text-red-700 dark:text-red-300 flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5" /> Not applicable — application was rejected.</p>
      ) : (
        <>
          {/* Unsigned */}
          <div className="mb-3">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-500 mb-1">Released by recruiter</p>
            {offer ? (
              <div className="flex items-center justify-between gap-2 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800/60 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <p className="text-xs text-slate-700 dark:text-zinc-200 truncate">{offer.filename}</p>
                  <p className="text-[10px] text-slate-500 dark:text-zinc-500">{(offer.size / 1024).toFixed(1)} KB · {new Date(offer.uploaded_at).toLocaleString()}</p>
                </div>
                <a href={resolveUrl(offer.url)} target="_blank" rel="noreferrer" className="text-[11px] text-sky-700 dark:text-sky-300 hover:underline" data-testid="offer-letter-view">Download</a>
              </div>
            ) : isRecruiter ? (
              canReleaseOffer ? (
                <>
                  <button onClick={() => unsignedRef.current?.click()} className="w-full py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 rounded-lg text-xs flex items-center justify-center gap-1.5" data-testid="recruiter-upload-offer">
                    <Upload className="w-3.5 h-3.5" /> Upload offer letter
                  </button>
                  <input ref={unsignedRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadUnsigned(f, ''); e.target.value = ''; }} />
                </>
              ) : (
                <p className="text-[11px] text-slate-500 dark:text-zinc-500">Select the candidate first to release an offer.</p>
              )
            ) : (
              <p className="text-[11px] text-slate-500 dark:text-zinc-500">Not released yet.</p>
            )}
          </div>

          {/* Signed */}
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-500 mb-1">Signed by candidate</p>
            {signed ? (
              <div className="flex items-center justify-between gap-2 bg-slate-50 dark:bg-[#0a0a0b] border border-emerald-500/30 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <p className="text-xs text-emerald-200 truncate">{signed.filename}</p>
                  <p className="text-[10px] text-slate-500 dark:text-zinc-500">{(signed.size / 1024).toFixed(1)} KB · {new Date(signed.uploaded_at).toLocaleString()}</p>
                </div>
                <a href={resolveUrl(signed.url)} target="_blank" rel="noreferrer" className="text-[11px] text-emerald-700 dark:text-emerald-300 hover:underline" data-testid="signed-offer-view">Download</a>
              </div>
            ) : isApplicant && offer ? (
              <>
                <button onClick={() => signedRef.current?.click()} className="w-full py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 rounded-lg text-xs flex items-center justify-center gap-1.5" data-testid="applicant-upload-signed">
                  <Upload className="w-3.5 h-3.5" /> Upload signed offer
                </button>
                <input ref={signedRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadSigned(f); e.target.value = ''; }} />
              </>
            ) : (
              <p className="text-[11px] text-slate-500 dark:text-zinc-500">{isApplicant ? 'Waiting for offer letter from recruiter.' : 'Awaiting candidate signature.'}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function RecruiterActions({ journey, actionLoading, onAdvance, onSelect, onReject, onRequestDocs, onComment, onSetStage, onStartAiScreening, openModal, modal }) {
  const finalized = journey.final_outcome === 'rejected' || journey.final_outcome === 'selected';
  const nextStageReachable = journey.current_stage !== 'offer_signed' && journey.final_outcome !== 'rejected';
  const canAdvance = nextStageReachable && journey.final_outcome !== 'selected'; // once selected, use specific actions
  // Bug #4 — show a dedicated "Start AI Screening" button when candidate is in the AI screening stage
  // and screening has not been scheduled/completed yet.
  const showStartAi = !finalized
    && journey.current_stage === 'ai_screening'
    && !journey.ai_screening_scheduled
    && !journey.ai_screening_completed;

  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-2xl p-5 mb-4" data-testid="recruiter-actions-panel">
      <h3 className="text-sm font-medium text-slate-600 dark:text-zinc-300 mb-3 flex items-center gap-1.5"><Building2 className="w-4 h-4 text-slate-500 dark:text-zinc-500" /> Recruiter actions</h3>

      <div className="flex flex-wrap gap-2">
        {showStartAi && (
          <button
            onClick={() => onStartAiScreening?.()}
            disabled={actionLoading}
            className="px-3 py-1.5 rounded-lg text-xs bg-blue-600 hover:bg-blue-500 text-white flex items-center gap-1.5 disabled:opacity-50"
            data-testid="action-start-ai-screening"
          >
            {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />} Start AI Screening
          </button>
        )}
        {canAdvance && (
          <button onClick={() => openModal({ type: 'advance' })} className="px-3 py-1.5 rounded-lg text-xs bg-sky-500/10 hover:bg-sky-500/20 text-sky-700 dark:text-sky-300 flex items-center gap-1.5" data-testid="action-advance">
            <ArrowRight className="w-3.5 h-3.5" /> Move to next stage
          </button>
        )}
        {!finalized && (
          <>
            <button onClick={() => openModal({ type: 'select' })} className="px-3 py-1.5 rounded-lg text-xs bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5" data-testid="action-select">
              <CheckCircle2 className="w-3.5 h-3.5" /> Select
            </button>
            <button onClick={() => openModal({ type: 'reject' })} className="px-3 py-1.5 rounded-lg text-xs bg-red-500/10 hover:bg-red-500/20 text-red-700 dark:text-red-300 flex items-center gap-1.5" data-testid="action-reject">
              <XCircle className="w-3.5 h-3.5" /> Reject
            </button>
          </>
        )}
        {journey.final_outcome === 'selected' && (
          <button onClick={() => openModal({ type: 'requestDocs' })} className="px-3 py-1.5 rounded-lg text-xs bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-300 flex items-center gap-1.5" data-testid="action-request-docs">
            <FileText className="w-3.5 h-3.5" /> Request documents
          </button>
        )}
        <button onClick={() => openModal({ type: 'comment' })} className="px-3 py-1.5 rounded-lg text-xs bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-600 dark:text-zinc-300 flex items-center gap-1.5" data-testid="action-comment">
          <MessageSquare className="w-3.5 h-3.5" /> Add comment
        </button>
        <button onClick={() => openModal({ type: 'setStage' })} className="px-3 py-1.5 rounded-lg text-xs bg-violet-500/10 hover:bg-violet-500/20 text-violet-700 dark:text-violet-300 flex items-center gap-1.5" data-testid="action-set-stage" title="Move candidate to any stage, reopen a rejection, or override the outcome">
          <Shuffle className="w-3.5 h-3.5" /> Manual override
        </button>
      </div>

      {modal && modal.type === 'setStage' ? (
        <SetStageModal
          journey={journey}
          loading={actionLoading}
          onClose={() => openModal(null)}
          onSubmit={(payload) => onSetStage(payload)}
        />
      ) : modal ? (
        <ActionModal
          modal={modal}
          loading={actionLoading}
          onClose={() => openModal(null)}
          onAdvance={onAdvance}
          onSelect={onSelect}
          onReject={onReject}
          onRequestDocs={onRequestDocs}
          onComment={onComment}
        />
      ) : null}
    </div>
  );
}


function SetStageModal({ journey, loading, onClose, onSubmit }) {
  const STAGES = [
    { key: 'matched',         label: 'Matched' },
    { key: 'screened',        label: 'Resume Matched' },
    { key: 'ai_screening',    label: 'AI Screening Round' },
    { key: 'decision',        label: 'Selected / Rejected' },
    { key: 'documents',       label: 'Documents Required' },
    { key: 'offer_released',  label: 'Offer Letter Released' },
    { key: 'offer_signed',    label: 'Signed Offer Letter' },
  ];
  const currentOutcome = journey.final_outcome; // 'selected' | 'rejected' | null
  const [stage, setStage] = React.useState(journey.current_stage || 'matched');
  const [decision, setDecision] = React.useState(currentOutcome === 'rejected' ? 'reopen' : (currentOutcome ? '' : ''));
  const [comment, setComment] = React.useState('');
  const [reason, setReason] = React.useState('');

  const submit = () => {
    if (!comment.trim()) return;
    onSubmit({
      stage,
      decision: decision || null,
      comment: comment.trim(),
      rejection_reason: decision === 'rejected' ? reason.trim() : '',
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="set-stage-modal">
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-2">
          <Shuffle className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">Manual override</h3>
        </div>
        <p className="text-slate-500 dark:text-zinc-500 text-xs mb-5">
          Move the candidate to any stage regardless of the pipeline. You can also un-reject or re-select.
          A short justification is required — it will be visible in the timeline for audit.
        </p>

        <div className="mb-3">
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-500 mb-1">Move to stage</label>
          <select value={stage} onChange={(e) => setStage(e.target.value)}
                  className="w-full bg-white dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
                  data-testid="set-stage-select">
            {STAGES.map(s => (<option key={s.key} value={s.key}>{s.label}</option>))}
          </select>
        </div>

        <div className="mb-3">
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-500 mb-1">Override outcome (optional)</label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { k: '',         label: 'Keep as-is',   cls: 'bg-slate-100 dark:bg-zinc-800' },
              { k: 'reopen',   label: 'Reopen',       cls: 'bg-sky-500/10 text-sky-700 dark:text-sky-300' },
              { k: 'selected', label: 'Mark selected', cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' },
              { k: 'rejected', label: 'Mark rejected', cls: 'bg-red-500/10 text-red-700 dark:text-red-300' },
            ].map(opt => (
              <button key={opt.k || 'keep'}
                onClick={() => setDecision(opt.k)}
                data-testid={`set-stage-decision-${opt.k || 'keep'}`}
                className={`px-2 py-1.5 rounded-lg text-xs font-medium border transition ${decision === opt.k ? 'border-violet-400 dark:border-violet-500 ring-1 ring-violet-400/60 ' + opt.cls : 'border-transparent ' + opt.cls}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {decision === 'rejected' && (
          <div className="mb-3">
            <label className="block text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-500 mb-1">Rejection reason (shown to candidate)</label>
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Skill mismatch on backend depth"
                   className="w-full bg-white dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
                   data-testid="set-stage-reject-reason" />
          </div>
        )}

        <div className="mb-4">
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-500 mb-1">Justification (required, shown in timeline)</label>
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3}
                    placeholder="Why are you overriding the stage? e.g. 'Rescheduling AI screening — candidate had connectivity issues'"
                    className="w-full bg-white dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white"
                    data-testid="set-stage-comment" />
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white" data-testid="set-stage-cancel">Cancel</button>
          <button onClick={submit} disabled={loading || !comment.trim()}
            className="px-3 py-1.5 bg-violet-500 hover:bg-violet-400 text-white text-sm rounded-lg disabled:opacity-50 flex items-center gap-1.5"
            data-testid="set-stage-submit">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shuffle className="w-3.5 h-3.5" />}
            Apply override
          </button>
        </div>
      </div>
    </div>
  );
}

function ActionModal({ modal, loading, onClose, onAdvance, onSelect, onReject, onRequestDocs, onComment }) {
  const [comment, setComment] = useState('');
  const [reason, setReason] = useState('');
  const [docs, setDocs] = useState('PAN Card\nAadhar Card\nLast 3 months payslips');

  const meta = {
    advance: { title: 'Move to next stage', confirm: 'Move forward', color: 'sky', danger: false, icon: ArrowRight },
    select: { title: 'Select candidate', confirm: 'Confirm selection', color: 'emerald', danger: false, icon: CheckCircle2 },
    reject: { title: 'Reject candidate', confirm: 'Confirm rejection', color: 'red', danger: true, icon: XCircle },
    requestDocs: { title: 'Request documents for offer', confirm: 'Send request', color: 'amber', danger: false, icon: FileText },
    comment: { title: 'Add a comment', confirm: 'Post comment', color: 'zinc', danger: false, icon: MessageSquare },
  }[modal.type] || {};

  const Icon = meta.icon || MessageSquare;

  const submit = () => {
    if (modal.type === 'advance') onAdvance(comment);
    else if (modal.type === 'select') onSelect(comment);
    else if (modal.type === 'reject') onReject(comment, reason);
    else if (modal.type === 'requestDocs') onRequestDocs(docs.split('\n').map(s => s.trim()).filter(Boolean), comment);
    else if (modal.type === 'comment') onComment(comment);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid={`modal-${modal.type}`}>
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2"><Icon className="w-4 h-4 text-slate-500 dark:text-zinc-400" /> {meta.title}</h3>
        <p className="text-slate-500 dark:text-zinc-500 text-xs mb-4">
          {modal.type === 'reject' && 'This will end the candidate\u2019s journey for this role. Add a reason if you\u2019d like the candidate to see it.'}
          {modal.type === 'select' && 'Selecting moves the candidate towards the offer phase.'}
          {modal.type === 'advance' && 'Add an optional note explaining the stage change.'}
          {modal.type === 'requestDocs' && 'List one document per line. The candidate will see this in their journey.'}
          {modal.type === 'comment' && 'Visible to both you and the candidate in the journey log.'}
        </p>

        {modal.type === 'requestDocs' && (
          <div className="mb-3">
            <label className="block text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-500 mb-1">Required documents</label>
            <textarea value={docs} onChange={e => setDocs(e.target.value)} rows={4} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" data-testid="docs-textarea" />
          </div>
        )}

        {modal.type === 'reject' && (
          <div className="mb-3">
            <label className="block text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-500 mb-1">Rejection reason (visible to candidate)</label>
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Did not meet technical bar"
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" data-testid="reject-reason-input" />
          </div>
        )}

        <div className="mb-4">
          <label className="block text-[11px] uppercase tracking-wide text-slate-500 dark:text-zinc-500 mb-1">{modal.type === 'comment' ? 'Comment' : 'Internal note (optional)'}</label>
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3} placeholder="Optional…"
            className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" data-testid="modal-comment-input" />
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm">Cancel</button>
          <button onClick={submit} disabled={loading || (modal.type === 'comment' && !comment.trim())} data-testid="modal-confirm"
            className={`flex-1 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1.5 ${
              meta.danger ? 'bg-red-500 hover:bg-red-400 text-white'
              : meta.color === 'emerald' ? 'bg-emerald-500 hover:bg-emerald-400 text-black'
              : meta.color === 'sky' ? 'bg-sky-500 hover:bg-sky-400 text-black'
              : meta.color === 'amber' ? 'bg-amber-400 hover:bg-amber-300 text-black'
              : 'bg-slate-900 dark:bg-white text-white dark:text-black'}`}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {meta.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}

function EventsTimeline({ events }) {
  if (!events || events.length === 0) return null;
  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/60 rounded-2xl p-5" data-testid="events-timeline">
      <h3 className="text-sm font-medium text-slate-600 dark:text-zinc-300 mb-3 flex items-center gap-1.5"><MessageSquare className="w-4 h-4 text-slate-500 dark:text-zinc-500" /> Activity & comments</h3>
      <ol className="space-y-3">
        {[...events].reverse().map((ev, i) => (
          <li key={i} className="flex items-start gap-3" data-testid={`event-${ev.event_type}-${i}`}>
            <span className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${ev.actor_role === 'recruiter' ? 'bg-sky-400' : ev.actor_role === 'applicant' ? 'bg-emerald-400' : 'bg-slate-200 dark:bg-zinc-600'}`} />
            <div className="flex-1">
              <p className="text-xs text-slate-600 dark:text-zinc-300">
                <span className="font-medium capitalize">{ev.actor_role}</span>{' '}
                <span className="text-slate-500 dark:text-zinc-500">{eventVerb(ev)}</span>
              </p>
              {ev.comment && <p className="text-xs text-slate-500 dark:text-zinc-400 italic mt-0.5">&ldquo;{ev.comment}&rdquo;</p>}
              {ev.rejection_reason && <p className="text-xs text-red-700 dark:text-red-300 mt-0.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {ev.rejection_reason}</p>}
              {ev.documents && <p className="text-[11px] text-slate-500 dark:text-zinc-500 mt-0.5">Requested: {ev.documents.join(', ')}</p>}
              {ev.document_name && <p className="text-[11px] text-slate-500 dark:text-zinc-500 mt-0.5">Document: {ev.document_name}</p>}
              {ev.offer_filename && <p className="text-[11px] text-slate-500 dark:text-zinc-500 mt-0.5">File: {ev.offer_filename}</p>}
              <p className="text-[10px] text-slate-500 dark:text-zinc-600 mt-0.5">{ev.at ? new Date(ev.at).toLocaleString() : ''}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function eventVerb(ev) {
  switch (ev.event_type) {
    case 'advance': return `moved candidate to ${ev.stage}`;
    case 'select': return 'selected the candidate';
    case 'reject': return `rejected at ${ev.stage}`;
    case 'request_documents': return 'requested documents';
    case 'upload_offer': return 'uploaded the offer letter';
    case 'upload_document': return 'uploaded a document';
    case 'sign_offer': return 'uploaded the signed offer';
    case 'comment': return 'left a comment';
    default: return ev.event_type;
  }
}
